import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "border-r border-[color:var(--border-border-base-soft,#e5e7eb)]" };
const _hoisted_2 = {
    class: "hidden lg:block h-full",
    "data-testid": "settings-sidebar"
};
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { SecondarySidebar } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
// Static JSON configuration for the menu
// TODO: In the future, this can be populated from moduleRegistry.getNavigationItems()
export default /*@__PURE__*/ _defineComponent({
    __name: 'SettingsSidebar',
    setup(__props) {
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const { can } = usePermissions();
        // Theme configuration - can be made dynamic or from user settings
        const sidebarTheme = ref("light");
        // Define the structure for menu sections (internal format)
        const menuSections = ref([
            {
                titleKey: "profile.title",
                items: [
                    {
                        labelKey: "profile.title",
                        path: "/profile",
                        testId: "settings-sidebar-item-profile",
                    },
                    {
                        labelKey: "profile.security",
                        path: "/profile/security",
                        testId: "settings-sidebar-item-security",
                    },
                    {
                        labelKey: "profile.permissions",
                        path: "/profile/permissions",
                        testId: "settings-sidebar-item-permissions",
                    },
                    {
                        labelKey: "profile.notifications",
                        path: "/profile/notifications",
                        testId: "settings-sidebar-item-notifications",
                    },
                ],
            },
            {
                titleKey: "workspace.title",
                items: [
                    {
                        labelKey: "workspace.settings.title",
                        path: "/workspace/settings",
                        testId: "settings-sidebar-item-workspace-settings",
                    },
                    {
                        labelKey: "accessManager.users.title",
                        path: "/workspace/users",
                        capability: "accessmanager.manage-users",
                        testId: "settings-sidebar-item-users",
                    },
                ],
            },
            {
                titleKey: "module.title",
                items: [
                    {
                        labelKey: "communicationProviders.title",
                        path: "/module/communication-providers",
                        capability: "opink.admin",
                        testId: "settings-sidebar-item-communication-providers",
                    },
                    {
                        labelKey: "consent.matrix.title",
                        path: "/module/consent-matrix",
                        capability: "opink.admin",
                        testId: "settings-sidebar-item-consent-matrix",
                    },
                    {
                        labelKey: "consentPurposeCatalogue.title",
                        path: "/module/consent-purposes",
                        capability: "opink.admin",
                        testId: "settings-sidebar-item-consent-purposes",
                    },
                    {
                        labelKey: "compliance.title",
                        path: "/module/compliance",
                        testId: "settings-sidebar-item-compliance",
                    },
                    {
                        // Same label as the MAIN sidebar's scheduling entry, by design (Figma 22715:26043) — that
                        // one is the social worker's agenda, this one the Access Manager's catalogue. Its own key,
                        // not a reuse of scheduling.pageTitle, so either wording can change without the other.
                        labelKey: "scheduling.slotTypes.menuLabel",
                        path: "/module/slot-types",
                        capability: "accessmanager.manage-users",
                        testId: "settings-sidebar-item-slot-types",
                    },
                    {
                        labelKey: "valueLists.menuLabel",
                        path: "/module/value-lists",
                        capability: "accessmanager.manage-users",
                        testId: "settings-sidebar-item-value-lists",
                    },
                    {
                        labelKey: "module.finance",
                        testId: "settings-sidebar-category-finance",
                        children: [
                            {
                                labelKey: "module.donations",
                                path: "/module/donations",
                                capability: "opdon.edit",
                                testId: "settings-sidebar-item-donations",
                            },
                        ],
                    },
                ],
            },
        ]);
        // Transform internal menu format to SecondarySidebar format
        // `can()` fails closed while the profile is still loading, so a gated entry appears only once
        // the capability set is known — briefly hiding an allowed entry is recoverable, advertising a
        // forbidden one is not.
        const isVisible = (item) => !item.capability || can(item.capability);
        // The longest entry path that is the route or one of its ancestors: a detail page
        // (/workspace/users/:userId) lights its list entry, while /profile/security lights Security only,
        // never also its prefix /profile.
        const activePath = computed(() => menuSections.value
            .flatMap((section) => section.items.flatMap((item) => [item, ...(item.children ?? [])]))
            .map((item) => item.path)
            .filter((path) => !!path && (route.path === path || route.path.startsWith(`${path}/`)))
            .reduce((longest, path) => (path.length > (longest?.length ?? -1) ? path : longest), undefined));
        const toSidebarItem = (item) => ({
            label: t(item.labelKey),
            active: !!item.path && item.path === activePath.value,
            testId: item.testId,
            onClick: () => {
                if (item.path) {
                    router.push(item.path);
                }
                closeSidebarOnMobile();
            },
        });
        const toVisibleSidebarItems = (items) => items.flatMap((item) => {
            if (!item.children)
                return isVisible(item) ? [toSidebarItem(item)] : [];
            const children = item.children.filter(isVisible).map(toSidebarItem);
            return children.length > 0
                ? [{ label: t(item.labelKey), key: item.labelKey, testId: item.testId, children }]
                : [];
        });
        const sidebarGroups = computed(() => {
            return menuSections.value
                .map((section) => ({
                label: t(section.titleKey),
                items: toVisibleSidebarItems(section.items),
            }))
                // A section whose every entry was filtered out would otherwise render as a bare heading.
                .filter((group) => group.items.length > 0);
        });
        const isSidebarOpen = ref(false);
        const toggleSidebar = () => {
            isSidebarOpen.value = !isSidebarOpen.value;
        };
        const closeSidebarOnMobile = () => {
            if (window.innerWidth < 1024) {
                isSidebarOpen.value = false;
            }
        };
        const handleItemClick = (_item, _group) => {
            // Item click is already handled via onClick in the transformed items.
            // This handler is kept as an extension point for future per-item logic.
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("button", {
                    class: "lg:hidden fixed top-20 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors",
                    "aria-label": "Toggle menu",
                    onClick: toggleSidebar
                }, [...(_cache[0] || (_cache[0] = [
                        _createElementVNode("i", { class: "ri-menu-line text-xl text-gray-700" }, null, -1 /* CACHED */)
                    ]))]),
                (isSidebarOpen.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30",
                        onClick: toggleSidebar
                    }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", {
                    class: _normalizeClass([
                        'lg:hidden fixed inset-y-0 left-0 z-40 h-screen transition-transform duration-300 ease-in-out',
                        isSidebarOpen.value ? 'translate-x-0' : '-translate-x-full',
                    ])
                }, [
                    _createElementVNode("button", {
                        class: "absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg z-50",
                        "aria-label": "Close menu",
                        onClick: toggleSidebar
                    }, [...(_cache[1] || (_cache[1] = [
                            _createElementVNode("i", { class: "ri-close-line text-xl text-gray-700" }, null, -1 /* CACHED */)
                        ]))]),
                    _createVNode(_unref(SecondarySidebar), {
                        title: _ctx.$t('settings.title'),
                        groups: sidebarGroups.value,
                        theme: sidebarTheme.value,
                        class: "h-full",
                        onItemClick: handleItemClick
                    }, null, 8 /* PROPS */, ["title", "groups", "theme"])
                ], 2 /* CLASS */),
                _createElementVNode("div", _hoisted_2, [
                    _createVNode(_unref(SecondarySidebar), {
                        title: _ctx.$t('settings.title'),
                        groups: sidebarGroups.value,
                        theme: sidebarTheme.value,
                        class: "h-full",
                        onItemClick: handleItemClick
                    }, null, 8 /* PROPS */, ["title", "groups", "theme"])
                ])
            ]));
        };
    }
});
